// One model for how the app looks (2026-10-20, docs/plans/theme-layers.md): a look is a
// theme (Horizon, a palette, a saved studio theme) plus a brightness (auto, light, dark,
// black; glass is a switch). Three layers set it, the most specific wins, and each layer
// sets the theme and the brightness on its own or passes them through:
// device (localStorage) over person (synced person_look) over workspace (the suite's
// own, or Everything's). Pure, so the Shell, the pickers and the tests agree.
import { CUSTOM_THEME, THEMES, type Hue, type PaletteRecord } from '@/lib/colour'
import type { Appearance } from '@/lib/horizon'

export const BRIGHTNESSES = ['auto', 'light', 'dark', 'black'] as const
export type Brightness = (typeof BRIGHTNESSES)[number]

export const LAYERS = ['device', 'person', 'workspace'] as const
export type LayerName = (typeof LAYERS)[number]

/** The theme a layer names. Absent in a layer means "pass through". */
export type ThemeRef =
  | { kind: 'horizon' }
  | { kind: 'palette'; theme: string; palette?: PaletteRecord }
  | { kind: 'studio'; profile?: string; spec: unknown }

/** What one layer says. Every field is optional: unset passes through to the layer below. */
export interface LayerSetting {
  theme?: ThemeRef
  colour?: Hue
  brightness?: Brightness
  glass?: boolean
}

export type Layers = Partial<Record<LayerName, LayerSetting | undefined>>

export type Origin = LayerName | 'default'

export interface ResolvedLook {
  theme: ThemeRef
  themeFrom: Origin
  brightness: Brightness
  brightnessFrom: Origin
  glass: boolean
  glassFrom: Origin
  /** the accent: the palette layer's own, else the most specific layer that has one */
  colour?: Hue
  /** the custom palette beneath a Horizon or studio theme (the studio reads it as its base) */
  palette?: PaletteRecord
  /** a palette has one brightness: brightness and glass do not apply */
  fixed: boolean
}

const HORIZON: ThemeRef = { kind: 'horizon' }

/** The order the layers are read in: the most specific first. */
const ORDER: LayerName[] = ['device', 'person', 'workspace']

const first = <T>(
  layers: Layers,
  pick: (l: LayerSetting) => T | undefined,
): { v: T; from: LayerName } | undefined => {
  for (const name of ORDER) {
    const l = layers[name]
    const v = l ? pick(l) : undefined
    if (v !== undefined) return { v, from: name }
  }
  return undefined
}

export const resolveLayers = (layers: Layers): ResolvedLook => {
  const theme = first(layers, (l) => l.theme)
  const brightness = first(layers, (l) => l.brightness)
  const glass = first(layers, (l) => l.glass)
  const ref = theme?.v ?? HORIZON
  const fixed = ref.kind === 'palette'
  // a palette brings its accent; otherwise the nearest layer's (the suite's accent rides on Horizon)
  const own = theme && ref.kind === 'palette' ? layers[theme.from]?.colour : undefined
  const colour = own ?? first(layers, (l) => l.colour)?.v
  const under = first(layers, (l) => (l.theme?.kind === 'palette' ? l.theme.palette : undefined))?.v
  return {
    theme: ref,
    themeFrom: theme?.from ?? 'default',
    brightness: brightness?.v ?? 'auto',
    brightnessFrom: brightness?.from ?? 'default',
    glass: glass?.v ?? false,
    glassFrom: glass?.from ?? 'default',
    colour,
    palette: under,
    fixed,
  }
}

/** The `Appearance` the Shell draws: a palette is `workspace`, glass wins over brightness. */
export const appearanceOf = (r: ResolvedLook): Appearance =>
  r.fixed ? 'workspace' : r.glass ? 'glass' : r.brightness

// -- patching one layer ---------------------------------------------------------------

/** `null` clears a field (back to pass-through); a missing key leaves it. */
export interface LayerPatch {
  theme?: ThemeRef | null
  colour?: Hue | null
  brightness?: Brightness | null
  glass?: boolean | null
}

export const patchLayer = (layer: LayerSetting | undefined, patch: LayerPatch): LayerSetting => {
  const next: LayerSetting = { ...layer }
  for (const k of ['theme', 'colour', 'brightness', 'glass'] as const) {
    if (!(k in patch)) continue
    const v = patch[k]
    if (v === null || v === undefined) delete next[k]
    else (next as Record<string, unknown>)[k] = v
  }
  return next
}

export const isEmptyLayer = (l: LayerSetting | undefined): boolean =>
  !l ||
  (l.theme === undefined && l.colour === undefined && l.brightness === undefined && l.glass === undefined)

// -- words ----------------------------------------------------------------------------

export const LAYER_TAB: Record<LayerName, string> = {
  device: 'This device',
  person: 'Me',
  workspace: 'This workspace',
}

const FROM: Record<Origin, string> = {
  device: 'this device',
  person: 'your own look',
  workspace: 'this workspace',
  default: 'the default',
}

export const BRIGHTNESS_LABEL: Record<Brightness, string> = {
  auto: 'Auto',
  light: 'Light',
  dark: 'Dark',
  black: 'Black',
}

/**
 * The top line: what you see and which layer each half comes from. `themeName` is the name of the
 * theme as a person reads it ("Halloween", "Horizon", a saved theme's name).
 */
export const seeLine = (r: ResolvedLook, themeName: string): string => {
  if (r.fixed) return `You see ${themeName}, from ${FROM[r.themeFrom]}. A palette has one brightness.`
  const bright = r.glass ? 'glass' : BRIGHTNESS_LABEL[r.brightness].toLowerCase()
  const bFrom = r.glass ? r.glassFrom : r.brightnessFrom
  if (bFrom === r.themeFrom) return `You see ${themeName}, ${bright}, both from ${FROM[r.themeFrom]}.`
  return `You see ${themeName} from ${FROM[r.themeFrom]}, ${bright} from ${FROM[bFrom]}.`
}

// -- stored shapes ----------------------------------------------------------------------

type Stored = {
  theme?: string
  palette?: Record<string, string>
  colour?: string
  brightness?: string
  glass?: boolean
}

const isBrightness = (v: unknown): v is Brightness => (BRIGHTNESSES as readonly unknown[]).includes(v)
const isTheme = (v: unknown): v is string =>
  typeof v === 'string' && ((THEMES as readonly string[]).includes(v) || v === CUSTOM_THEME)

/** A workspace record's (or Everything's `look`) fields as a layer. Unset theme is Horizon, the default. */
export const layerOfCarrier = (c: Stored | undefined): LayerSetting => {
  const out: LayerSetting = {}
  if (c?.theme && isTheme(c.theme))
    out.theme = {
      kind: 'palette',
      theme: c.theme,
      palette: c.theme === CUSTOM_THEME ? (c.palette as PaletteRecord | undefined) : undefined,
    }
  if (c?.colour) out.colour = c.colour as Hue
  if (isBrightness(c?.brightness)) out.brightness = c.brightness
  if (typeof c?.glass === 'boolean') out.glass = c.glass
  return out
}

/** A layer as the fields a Workspace or `Settings.look` keeps; `null` for a field to clear on a patch. */
export const carrierOfLayer = (l: LayerSetting): Record<string, unknown> => {
  const t = l.theme?.kind === 'palette' ? l.theme : undefined
  return {
    theme: t?.theme ?? null,
    palette: t && t.theme === CUSTOM_THEME && t.palette ? t.palette : null,
    colour: l.colour ?? null,
    brightness: l.brightness ?? null,
    glass: l.glass ?? null,
  }
}

/** `Settings.look` as stored: only what is set. */
export const lookStoreOfLayer = (l: LayerSetting): Record<string, unknown> =>
  Object.fromEntries(Object.entries(carrierOfLayer(l)).filter(([, v]) => v !== null))

/** One `person_look` entry (`all` or `suites[slug]`, v1 or v2) as a layer; `horizon` and `studio` are its extras. */
export const layerOfEntry = (e: (Stored & { studio?: unknown }) | undefined): LayerSetting => {
  const out = layerOfCarrier(e)
  const s = e?.studio as { profile?: unknown; spec?: unknown } | undefined
  if (s && typeof s === 'object' && typeof s.spec === 'object' && s.spec !== null)
    out.theme = {
      kind: 'studio',
      profile: typeof s.profile === 'string' ? s.profile : undefined,
      spec: s.spec,
    }
  else if (e?.theme === 'horizon') out.theme = { kind: 'horizon' }
  return out
}

/** A layer as a `person_look` entry; the studio theme goes under `studio`. */
export const entryOfLayer = (l: LayerSetting): Record<string, unknown> => {
  const out: Record<string, unknown> = {}
  const t = l.theme
  if (t?.kind === 'horizon') out.theme = 'horizon'
  else if (t?.kind === 'palette') {
    out.theme = t.theme
    if (t.theme === CUSTOM_THEME && t.palette && Object.keys(t.palette).length) out.palette = t.palette
  } else if (t?.kind === 'studio')
    out.studio = t.profile ? { profile: t.profile, spec: t.spec } : { spec: t.spec }
  if (l.colour) out.colour = l.colour
  if (l.brightness) out.brightness = l.brightness
  if (l.glass !== undefined) out.glass = l.glass
  return out
}

// -- the device layer ---------------------------------------------------------------------

/**
 * The device layer from the old per-device mode (`appearance`): `workspace` was "show the palette", so
 * it is no override; the others are a brightness (glass is the switch). A device that never chose but
 * has used the app (`used`) was on Auto. `null` is a device with nothing to carry over.
 */
export const deviceFromLegacy = (mode: string | null, used: boolean): LayerSetting => {
  const m = mode ?? (used ? 'auto' : null)
  if (m === 'auto' || m === 'light' || m === 'dark' || m === 'black') return { brightness: m }
  if (m === 'glass') return { brightness: 'auto', glass: true }
  return {}
}

// -- who may write which layer ---------------------------------------------------------------

/**
 * The layers a person can write. The device is anyone's. The person layer is synced Settings, which the
 * owner and an admin member have (a plain member's stay on the device). The workspace layer is whoever
 * may edit the suite today: the owner; an admin member's edits stay her own, and a member's are not hers.
 */
export const writableLayers = (who: { member: boolean; admin: boolean }): LayerName[] => [
  'device',
  ...(!who.member || who.admin ? (['person'] as const) : []),
  ...(!who.member && !who.admin ? (['workspace'] as const) : []),
]

/** The first row of a layer's half: the words for passing through to the layers below it. */
export const passThrough = (
  layer: LayerName,
  hasPerson: boolean,
): { label: string; hint: string } | undefined => {
  if (layer === 'workspace') return undefined
  if (layer === 'device' && hasPerson)
    return { label: 'Use mine', hint: 'Follow my own look, then the workspace’s' }
  return { label: 'Use the workspace’s', hint: 'Follow what this workspace has set' }
}
